'use client';

import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ExternalLink, FileText, MousePointerClick, Quote, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { RefreshStatus } from '@/components/shared/RefreshStatus';
import type { PageReviewSummary } from '@/lib/page-review/client';

export interface PageInventoryRow<T = unknown> {
  id: string;
  crawlId: string;
  url: string;
  title: string | null;
  description: string | null;
  ogImage: string | null;
  seoGrade: string | null;
  wordCount: number | null;
  httpStatus: number | null;
  citationCount: number;
  searchImpressions: number;
  searchClicks: number;
  sessions: number;
  opportunityLabel?: string | null;
  hasHtml: boolean;
  review: PageReviewSummary | null;
  source: T;
}

export type PageInventorySortColumn = 'citations' | 'impressions' | 'clicks';

interface PagesInventoryTableProps<T> {
  rows: PageInventoryRow<T>[];
  loading?: boolean;
  sortColumn?: PageInventorySortColumn | null;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: PageInventorySortColumn) => void;
  onOpenPage: (row: PageInventoryRow<T>) => void;
}

interface CompactPagesInventoryTableProps<T> {
  rows: PageInventoryRow<T>[];
  selectedPageId?: string | null;
  onSelectPage: (row: PageInventoryRow<T>) => void;
}

const GRADE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  A: { bg: 'status.success.bg', text: 'status.success.fg', border: 'status.success.border' },
  B: { bg: 'status.info.bg', text: 'status.info.fg', border: 'status.info.border' },
  C: { bg: 'status.warning.bg', text: 'status.warning.fg', border: 'status.warning.border' },
  D: { bg: 'status.warning.bg', text: 'status.warning.fg', border: 'status.warning.border' },
  F: { bg: 'status.danger.bg', text: 'status.danger.fg', border: 'status.danger.border' },
};

const FULL_GRID = '42px 36px minmax(0, 1.3fr) minmax(0, .9fr) 42px 48px minmax(170px, .85fr)';

function metric(value: number) {
  if (!value) return '—';
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);
}

function pathFor(url: string) {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || '/';
  } catch {
    return url;
  }
}

function ReviewStateBadge({ review }: { review: PageReviewSummary | null }) {
  if (!review) return null;
  const palette =
    review.status === 'completed' ? 'green' : review.status === 'failed' ? 'red' : 'gray';
  const label =
    review.status === 'completed'
      ? `Reviewed · v${review.version}`
      : review.status === 'failed'
        ? 'Failed'
        : 'Reviewing';
  return (
    <Badge size="xs" colorPalette={palette} variant="subtle">
      {label}
    </Badge>
  );
}

function PagePreview({ row }: { row: PageInventoryRow }) {
  if (row.ogImage) {
    return (
      <Image
        src={row.ogImage}
        alt={row.title || 'Page preview'}
        w="40px"
        h="30px"
        objectFit="cover"
        borderRadius="sm"
      />
    );
  }
  return (
    <Flex w="40px" h="28px" bg="ui.surface.selected" align="center" justify="center">
      <FileText size={14} color="var(--chakra-colors-ui-text-tertiary)" />
    </Flex>
  );
}

function Grade({ value }: { value: string | null }) {
  const grade = value && GRADE_COLORS[value] ? value : '—';
  const colors =
    grade === '—'
      ? { bg: 'ui.surface.input', text: 'ui.text.tertiary', border: 'ui.border.subtle' }
      : GRADE_COLORS[grade];
  return (
    <Flex
      display="inline-flex"
      w="28px"
      h="28px"
      align="center"
      justify="center"
      bg={colors.bg}
      borderWidth="1px"
      borderColor={colors.border}
      fontSize="xs"
      fontWeight="700"
      color={colors.text}
    >
      {grade}
    </Flex>
  );
}

function SortHeading({
  label,
  column,
  activeColumn,
  order,
  onSort,
  icon,
}: {
  label: string;
  column: PageInventorySortColumn;
  activeColumn?: PageInventorySortColumn | null;
  order?: 'asc' | 'desc';
  onSort?: (column: PageInventorySortColumn) => void;
  icon: ReactNode;
}) {
  return (
    <Button
      variant="plain"
      h="auto"
      p={0}
      w="full"
      onClick={() => onSort?.(column)}
      aria-label={`Sort pages by ${label.toLowerCase()}`}
      disabled={!onSort}
    >
      <HStack justify="flex-end" gap={1} color="ui.text.secondary">
        {icon}
        <Text fontSize="10px" fontWeight="500" textTransform="uppercase">
          {label}
        </Text>
        {activeColumn === column ? <Text fontSize="9px">{order === 'asc' ? '↑' : '↓'}</Text> : null}
      </HStack>
    </Button>
  );
}

export function PagesInventoryTable<T>({
  rows,
  loading = false,
  sortColumn,
  sortOrder,
  onSort,
  onOpenPage,
}: PagesInventoryTableProps<T>) {
  return (
    <Box position="relative" aria-busy={loading || undefined}>
      <HStack h="6" mb="1" justify="flex-end">
        <RefreshStatus active={Boolean(loading && rows.length > 0)} />
      </HStack>

      <VStack display={{ base: 'flex', md: 'none' }} align="stretch" gap={0}>
        {loading && rows.length === 0 ? (
          <Flex minH="160px" align="center" justify="center">
            <InlineSpinner size="md" label="Loading pages" />
          </Flex>
        ) : null}
        {rows.map(row => (
          <Box
            key={row.id}
            py={3}
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
            cursor="pointer"
            role="link"
            tabIndex={0}
            onClick={() => onOpenPage(row)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenPage(row);
              }
            }}
            _hover={{ bg: 'ui.surface.selected' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'accent.fg',
              outlineOffset: '2px',
            }}
          >
            <HStack align="start" gap={3}>
              <PagePreview row={row} />
              <Box minW={0} flex="1">
                <Text fontSize="sm" fontWeight="700" lineClamp={2}>
                  {row.title || pathFor(row.url)}
                </Text>
                <Text mt={1} fontSize="xs" color="ui.text.tertiary" truncate>
                  {pathFor(row.url)}
                </Text>
                <HStack mt={2} gap={1.5} flexWrap="wrap">
                  <Grade value={row.seoGrade} />
                  <Badge colorPalette={row.httpStatus === 200 ? 'green' : 'orange'}>
                    {row.httpStatus || '—'}
                  </Badge>
                  <ReviewStateBadge review={row.review} />
                </HStack>
              </Box>
            </HStack>
            <Grid templateColumns="repeat(4, minmax(0, 1fr))" mt={3} gap={2}>
              {[
                ['Words', row.wordCount?.toLocaleString() || '—'],
                ['Cites', metric(row.citationCount)],
                ['Impr', metric(row.searchImpressions)],
                ['Clicks', metric(row.searchClicks)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Text fontSize="2xs" color="ui.text.tertiary" fontWeight="700">
                    {label}
                  </Text>
                  <Text fontSize="sm" fontWeight="650">
                    {value}
                  </Text>
                </Box>
              ))}
            </Grid>
          </Box>
        ))}
      </VStack>

      <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
        <Grid templateColumns={FULL_GRID} gap={2} px={2} py={2} borderBottomWidth="1px">
          {['Page', 'Score', 'Title', 'URL', 'Words', 'Status'].map((label, index) => (
            <Text
              key={label}
              fontSize="10px"
              fontWeight="500"
              color="ui.text.secondary"
              textTransform="uppercase"
              textAlign={index === 1 || index === 5 ? 'center' : index === 4 ? 'right' : 'left'}
            >
              {label}
            </Text>
          ))}
          <GridItem pl={2} borderLeftWidth="1px" borderColor="ui.border.subtle">
            <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
              <SortHeading
                label="Citations"
                column="citations"
                activeColumn={sortColumn}
                order={sortOrder}
                onSort={onSort}
                icon={<Quote size={10} />}
              />
              <SortHeading
                label="Impr"
                column="impressions"
                activeColumn={sortColumn}
                order={sortOrder}
                onSort={onSort}
                icon={<Search size={10} />}
              />
              <SortHeading
                label="Clicks"
                column="clicks"
                activeColumn={sortColumn}
                order={sortOrder}
                onSort={onSort}
                icon={<MousePointerClick size={10} />}
              />
            </Grid>
          </GridItem>
        </Grid>

        <VStack align="stretch" gap={0}>
          {loading && rows.length === 0 ? (
            <Flex minH="160px" align="center" justify="center">
              <InlineSpinner size="md" label="Loading pages" />
            </Flex>
          ) : null}
          {rows.map(row => (
            <Grid
              key={row.id}
              templateColumns={FULL_GRID}
              gap={2}
              px={2}
              py={2.5}
              minH="64px"
              alignItems="center"
              borderBottomWidth="1px"
              borderColor="ui.border.subtle"
              contentVisibility="auto"
              cursor="pointer"
              role="link"
              tabIndex={0}
              onClick={() => onOpenPage(row)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpenPage(row);
                }
              }}
              _hover={{ bg: 'ui.surface.selected' }}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'accent.fg',
                outlineOffset: '-2px',
              }}
            >
              <PagePreview row={row} />
              <GridItem textAlign="center">
                <Grade value={row.seoGrade} />
              </GridItem>
              <Box minW={0}>
                <HStack gap="2" minW={0}>
                  <Text fontSize="sm" truncate minW={0} flex="1">
                    {row.title || 'Untitled page'}
                  </Text>
                  <ReviewStateBadge review={row.review} />
                </HStack>
                {row.description ? (
                  <Text fontSize="xs" color="ui.text.secondary" truncate>
                    {row.description}
                  </Text>
                ) : null}
              </Box>
              <HStack minW={0} gap={1}>
                <Text fontSize="xs" truncate flex="1">
                  {pathFor(row.url)}
                </Text>
                <Link
                  href={row.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${row.title || row.url}`}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                >
                  <ExternalLink size={12} />
                </Link>
              </HStack>
              <Text fontSize="xs" textAlign="right">
                {row.wordCount?.toLocaleString() || '—'}
              </Text>
              <Badge colorPalette={row.httpStatus === 200 ? 'green' : 'orange'} textAlign="center">
                {row.httpStatus || '—'}
              </Badge>
              <GridItem pl={2} borderLeftWidth="1px" borderColor="ui.border.subtle">
                <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
                  <Text
                    fontSize="sm"
                    textAlign="right"
                    color={row.citationCount ? 'data.purple' : 'ui.text.tertiary'}
                  >
                    {metric(row.citationCount)}
                  </Text>
                  <Text fontSize="sm" textAlign="right" color="ui.text.secondary">
                    {metric(row.searchImpressions)}
                  </Text>
                  <Text fontSize="sm" textAlign="right">
                    {metric(row.searchClicks)}
                  </Text>
                </Grid>
              </GridItem>
            </Grid>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}

export function CompactPagesInventoryTable<T>({
  rows,
  selectedPageId,
  onSelectPage,
}: CompactPagesInventoryTableProps<T>) {
  return (
    <Box overflowX="auto">
      <Grid
        templateColumns="minmax(190px, 1fr) 108px"
        gap={2}
        px={4}
        py={2}
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
      >
        {['Page', 'Evidence'].map(label => (
          <Text
            key={label}
            fontSize="10px"
            fontWeight="700"
            color="ui.text.tertiary"
            textTransform="uppercase"
          >
            {label}
          </Text>
        ))}
      </Grid>
      <VStack align="stretch" gap={0} minW="390px">
        {rows.map((row, index) => (
          <Grid
            key={row.id}
            templateColumns="minmax(190px, 1fr) 108px"
            gap={2}
            px={4}
            py={3}
            alignItems="center"
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
            bg={row.id === selectedPageId ? 'ui.surface.selected' : 'transparent'}
            _hover={{ bg: 'ui.surface.selected' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'accent.fg',
              outlineOffset: '-2px',
            }}
            cursor="pointer"
            role="link"
            tabIndex={0}
            onClick={() => onSelectPage(row)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectPage(row);
              }
            }}
            contentVisibility="auto"
          >
            <HStack minW={0} align="start" gap={2.5}>
              <Flex
                w="24px"
                h="24px"
                flex="0 0 auto"
                align="center"
                justify="center"
                bg={row.id === selectedPageId ? 'accent.solid' : 'ui.surface.input'}
                color={row.id === selectedPageId ? 'accent.contrast' : 'ui.text.secondary'}
                fontSize="10px"
                fontWeight="800"
              >
                {index + 1}
              </Flex>
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="750" truncate>
                  {row.title || pathFor(row.url)}
                </Text>
                <Text fontSize="xs" color="ui.text.tertiary" truncate>
                  {pathFor(row.url)}
                </Text>
                <HStack mt={1} gap={1}>
                  {row.opportunityLabel ? (
                    <Badge size="xs" variant="outline">
                      {row.opportunityLabel}
                    </Badge>
                  ) : null}
                  <ReviewStateBadge review={row.review} />
                </HStack>
              </Box>
            </HStack>
            <Grid
              templateColumns="repeat(3, 1fr)"
              gap={1}
              title="Impressions · citations · sessions"
            >
              {[row.searchImpressions, row.citationCount, row.sessions].map(
                (value, metricIndex) => (
                  <Box key={metricIndex} textAlign="right">
                    <Text fontSize="xs" fontWeight="650">
                      {metric(value)}
                    </Text>
                    <Text fontSize="9px" color="ui.text.tertiary">
                      {metricIndex === 0 ? 'I' : metricIndex === 1 ? 'C' : 'S'}
                    </Text>
                  </Box>
                )
              )}
            </Grid>
          </Grid>
        ))}
      </VStack>
    </Box>
  );
}
